import { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [activeModal, setActiveModal] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const cursorRef = useRef(null);
  const meshRef = useRef(null);
  const targetPos = useRef({ x: 0, y: 0 });
  const currentPos = useRef({ x: 0, y: 0 });
  const blobPos = useRef({ x: 0, y: 0 });

  // Calculator state
  const [projectType, setProjectType] = useState('api');
  const [complexity, setComplexity] = useState('medium');
  const [timeline, setTimeline] = useState('normal');

  // Form state
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState('');

  // Mouse tracking with smooth follow
  useEffect(() => {
    let animationId;

    const handleMouseMove = (e) => {
      targetPos.current = { x: e.clientX, y: e.clientY };
    };

    const animate = () => {
      // Cursor
      currentPos.current.x += (targetPos.current.x - currentPos.current.x) * 0.12;
      currentPos.current.y += (targetPos.current.y - currentPos.current.y) * 0.12;

      if (cursorRef.current) {
        cursorRef.current.style.left = `${currentPos.current.x - 6}px`;
        cursorRef.current.style.top = `${currentPos.current.y - 6}px`;
      }

      // Blobs - slower, smoother
      const targetBlobX = (targetPos.current.x / window.innerWidth - 0.5) * 100;
      const targetBlobY = (targetPos.current.y / window.innerHeight - 0.5) * 100;
      blobPos.current.x += (targetBlobX - blobPos.current.x) * 0.02;
      blobPos.current.y += (targetBlobY - blobPos.current.y) * 0.02;

      if (meshRef.current) {
        meshRef.current.style.setProperty('--mx', `${blobPos.current.x}px`);
        meshRef.current.style.setProperty('--my', `${blobPos.current.y}px`);
      }

      animationId = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openModal = (modalType) => setActiveModal(modalType);
  const closeModal = () => setActiveModal(null);

  const skills = {
    'Languages': ['Go', 'Python', 'Node.js', 'TypeScript', 'Rust'],
    'Databases': ['PostgreSQL', 'MongoDB', 'Redis', 'ClickHouse', 'Elasticsearch'],
    'Infrastructure': ['Kubernetes', 'Docker', 'Terraform', 'AWS', 'GCP'],
    'Messaging': ['Kafka', 'RabbitMQ', 'NATS', 'gRPC'],
  };

  const baseRates = { api: 3000, microservices: 8000, data: 6000, consulting: 2000 };
  const complexityMultiplier = { simple: 0.7, medium: 1, complex: 1.5, enterprise: 2.5 };
  const timelineMultiplier = { relaxed: 0.9, normal: 1, urgent: 1.3, asap: 1.6 };

  const calculateEstimate = () => {
    const base = baseRates[projectType];
    const estimate = base * complexityMultiplier[complexity] * timelineMultiplier[timeline];
    return Math.round(estimate / 100) * 100;
  };

  const projectLabels = {
    api: 'API Development',
    microservices: 'Microservices',
    data: 'Data Pipeline',
    consulting: 'Consulting'
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    setTimeout(() => {
      setFormStatus('sent');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus(''), 3000);
    }, 1000);
  };

  return (
    <div className="app">
      {/* Cursor */}
      <div ref={cursorRef} className="cursor" />

      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
        <a href="#home" className="nav__logo">AZ</a>
        <div className="nav__links">
          <a href="#expertise">Expertise</a>
          <a href="#stack">Stack</a>
          <a href="#estimate">Estimate</a>
          <a href="#contact">Contact</a>
        </div>
      </nav>

      {/* Hero */}
      <section id="home" className="hero">
        {/* Gradient Mesh Background */}
        <div className="hero__mesh" ref={meshRef}>
          <div className="blob blob--1" />
          <div className="blob blob--2" />
          <div className="blob blob--3" />
          <div className="noise" />
        </div>

        <div className="hero__content">
          <p className="hero__label">Senior Backend Engineer</p>
          <h1 className="hero__title">
            Alex<br />Zhigulev
          </h1>
          <p className="hero__desc">
            Building high-load distributed systems.<br />
            Microservices, APIs, and infrastructure at scale.
          </p>
          <div className="hero__stats">
            <div className="hero__stat">
              <span className="hero__stat-value">7+</span>
              <span className="hero__stat-label">Years Experience</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-value">100+</span>
              <span className="hero__stat-label">Projects Delivered</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-value">1M+</span>
              <span className="hero__stat-label">RPS Handled</span>
            </div>
          </div>
        </div>

        <div className="hero__scroll">
          <span>Scroll</span>
          <div className="hero__scroll-line"></div>
        </div>
      </section>

      {/* Expertise */}
      <section id="expertise" className="expertise">
        <div className="container">
          <h2 className="section-label">Expertise</h2>
          <div className="expertise__grid">
            <div className="expertise__item">
              <span className="expertise__number">01</span>
              <h3>Distributed Systems</h3>
              <p>Designing fault-tolerant architectures that handle millions of requests. Event-driven systems, CQRS, and eventual consistency patterns.</p>
            </div>
            <div className="expertise__item">
              <span className="expertise__number">02</span>
              <h3>API Development</h3>
              <p>RESTful APIs, GraphQL, and gRPC services. Rate limiting, authentication, versioning, and comprehensive documentation.</p>
            </div>
            <div className="expertise__item">
              <span className="expertise__number">03</span>
              <h3>Data Engineering</h3>
              <p>ETL pipelines, real-time data processing, and analytics infrastructure. Working with petabyte-scale datasets.</p>
            </div>
            <div className="expertise__item">
              <span className="expertise__number">04</span>
              <h3>DevOps & SRE</h3>
              <p>CI/CD pipelines, infrastructure as code, monitoring, and observability. Keeping systems reliable at scale.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section id="stack" className="stack">
        <div className="container">
          <h2 className="section-label">Tech Stack</h2>
          <div className="stack__grid">
            {Object.entries(skills).map(([category, items]) => (
              <div key={category} className="stack__category">
                <h3 className="stack__category-title">{category}</h3>
                <div className="stack__items">
                  {items.map(item => (
                    <span key={item} className="stack__item">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Estimate Calculator */}
      <section id="estimate" className="estimate">
        <div className="container">
          <h2 className="section-label">Project Estimate</h2>
          <div className="estimate__wrapper">
            <div className="estimate__form">
              <div className="estimate__group">
                <label className="estimate__label">Project Type</label>
                <div className="estimate__options">
                  {Object.entries(projectLabels).map(([key, label]) => (
                    <button
                      key={key}
                      className={`estimate__option ${projectType === key ? 'estimate__option--active' : ''}`}
                      onClick={() => setProjectType(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="estimate__group">
                <label className="estimate__label">Complexity</label>
                <div className="estimate__options">
                  {['simple', 'medium', 'complex', 'enterprise'].map((level) => (
                    <button
                      key={level}
                      className={`estimate__option ${complexity === level ? 'estimate__option--active' : ''}`}
                      onClick={() => setComplexity(level)}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="estimate__group">
                <label className="estimate__label">Timeline</label>
                <div className="estimate__options">
                  {[
                    { key: 'relaxed', label: 'Relaxed' },
                    { key: 'normal', label: 'Normal' },
                    { key: 'urgent', label: 'Urgent' },
                    { key: 'asap', label: 'ASAP' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      className={`estimate__option ${timeline === key ? 'estimate__option--active' : ''}`}
                      onClick={() => setTimeline(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="estimate__result">
              <span className="estimate__result-label">Estimated starting from</span>
              <span className="estimate__result-value">${calculateEstimate().toLocaleString()}</span>
              <span className="estimate__result-note">Final price depends on detailed requirements</span>
              <a href="#contact" className="estimate__cta">Get exact quote</a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="about">
        <div className="container">
          <div className="about__content">
            <h2 className="section-label">About</h2>
            <p className="about__text">
              I architect and build backend systems that power products at scale.
              From early-stage startups to enterprise solutions — I bring deep
              technical expertise and a pragmatic approach to solving complex problems.
            </p>
            <div className="about__highlights">
              <div className="about__highlight">
                <span className="about__highlight-icon">→</span>
                <span>High-load systems optimization</span>
              </div>
              <div className="about__highlight">
                <span className="about__highlight-icon">→</span>
                <span>Microservices architecture design</span>
              </div>
              <div className="about__highlight">
                <span className="about__highlight-icon">→</span>
                <span>Technical leadership & mentoring</span>
              </div>
              <div className="about__highlight">
                <span className="about__highlight-icon">→</span>
                <span>Code review & architecture audits</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-label">Contact</h2>
          <div className="contact__wrapper">
            <div className="contact__info">
              <p className="contact__text">Let's build something great</p>
              <a href="mailto:me@azg.dev" className="contact__email">me@azg.dev</a>
            </div>

            <form className="contact__form" onSubmit={handleSubmit}>
              <div className="form__group">
                <input
                  type="text"
                  placeholder="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="form__input"
                />
              </div>
              <div className="form__group">
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="form__input"
                />
              </div>
              <div className="form__group">
                <textarea
                  placeholder="Tell me about your project..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  className="form__input form__textarea"
                />
              </div>
              <button
                type="submit"
                className={`form__submit ${formStatus === 'sending' ? 'form__submit--loading' : ''}`}
                disabled={formStatus === 'sending'}
              >
                {formStatus === 'sending' ? 'Sending...' : formStatus === 'sent' ? 'Sent!' : 'Send message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer__top">
            <span className="footer__name">Aleksei Zhigulev</span>
            <div className="footer__links">
              <button onClick={() => openModal('privacy')}>Privacy</button>
              <button onClick={() => openModal('terms')}>Terms</button>
              <button onClick={() => openModal('refund')}>Refund</button>
            </div>
          </div>
          <p className="footer__copy">&copy; {new Date().getFullYear()}</p>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal__close" onClick={closeModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            {activeModal === 'privacy' && (
              <>
                <h2>Privacy Policy</h2>
                <div className="modal__content">
                  <p className="modal__date">Last updated: January 2025</p>
                  <h3>Information Collection</h3>
                  <p>When engaging my services, I collect necessary information including name, email, and project details to provide software development services.</p>
                  <h3>Use of Information</h3>
                  <p>Information is used for project communication, service delivery, payment processing, and invoicing. Data is never sold to third parties.</p>
                  <h3>Third-Party Services</h3>
                  <p>Payments are processed through Stripe. Their privacy policy governs payment data.</p>
                  <h3>Your Rights</h3>
                  <p>You may request access, correction, or deletion of your data at me@azg.dev</p>
                </div>
              </>
            )}

            {activeModal === 'terms' && (
              <>
                <h2>Terms of Service</h2>
                <div className="modal__content">
                  <p className="modal__date">Last updated: January 2025</p>
                  <h3>Services</h3>
                  <p>I provide backend development, system architecture, API development, and technical consulting as defined in project agreements.</p>
                  <h3>Payment</h3>
                  <p>Terms are specified per project. Invoices are due within 14 days unless otherwise agreed.</p>
                  <h3>Intellectual Property</h3>
                  <p>Upon full payment, clients own custom code developed for their project. Pre-existing code remains with original owners.</p>
                  <h3>Liability</h3>
                  <p>Liability is limited to the amount paid for services.</p>
                  <h3>Contact</h3>
                  <p>Questions: me@azg.dev</p>
                </div>
              </>
            )}

            {activeModal === 'refund' && (
              <>
                <h2>Refund Policy</h2>
                <div className="modal__content">
                  <p className="modal__date">Last updated: January 2025</p>
                  <h3>Before Work Begins</h3>
                  <p>Full refund of deposits if cancelled before work starts.</p>
                  <h3>Work in Progress</h3>
                  <p>Payment due for completed work. Remaining prepaid amounts refunded.</p>
                  <h3>Completed Work</h3>
                  <p>Delivered work meeting specifications is non-refundable.</p>
                  <h3>Process</h3>
                  <p>Approved refunds processed within 5-10 business days via original payment method.</p>
                  <h3>Contact</h3>
                  <p>Refund requests: me@azg.dev</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
